import CloseIcon from '@mui/icons-material/Close';
import * as Yup from 'yup';
import { Button, Dialog, DialogContent, DialogTitle, Grid, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { ReactHookForm } from '../../../components/hook-form/react-hook-form.component';
import { useReactHookForm } from '../../../hooks/useReactHookForm';
import { useAuthorize } from '../../../hooks/use-authorize';
import { UserPermissionName } from '../../../models/user-permission-name';
import { parseErrorMessageRecursive } from '../../../utils/yup-error-parser.util';
import { useSnackbar } from '../../../hooks/use-snackbar.hook';
import { servicesApiKeyStore } from '../../../api/masters/services-api-key-store';
import { FormSelectDynamic } from '../../../components/hook-form/form-select.dynamic.component';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { useEffect, useState } from 'react';
import { FormSelect } from '../../../components/hook-form/form-select.component';
import { LoadingIndicator } from '../../../components/loading-indicator.component';
import { useSaveData } from '../../../hooks/useSaveData';

interface QuickBookServiceDialog {
    serviceID: any;
    qboServiceID: any;
    reinspectionQBOServiceID: any;
    openDialog: boolean;
    onClosed: () => void;
}

const styling = {
    dialogTitleStyle: { padding: '16px 24px 0' },
    contentPadding: { paddingTop: '30px' },
};

export const QuickBookServiceDialog = ({ serviceID, qboServiceID, reinspectionQBOServiceID, openDialog, onClosed }: QuickBookServiceDialog) => {

    console.log("reinspectionQBOServiceID", reinspectionQBOServiceID);
    const quickbookFormSchema = Yup.object().shape({
        qboServiceID: Yup.string().required('QuickBooks Service is required'),
        reinspectionQboServiceID: Yup.string().required('Reinspection QuickBooks Service is required'),
    });
    const { showSnackbar } = useSnackbar();
    const [quickbookOption, setQuickbookOption] = useState<any[]>([]);
    const [quickbookServiceData, setQuickBookServiceData] = useState<any>();
    const [reinspectionServiceData, setReinspectionServiceData] = useState<any>();

    const form = useReactHookForm({
        defaultValues: {
            qboServiceID: 'N/A',
            reinspectionQboServiceID: 'N/A'

        },
        // yupSchema: quickbookFormSchema
    });

    const { data, isLoading } = useLoadData<ApiResponse<any[]>>(servicesApiKeyStore.getQBOServices());

    const { isLoading: isQBOMappingLoading, mutateAsync } = useSaveData<any>(servicesApiKeyStore.updateQBOMapping(),
        () => servicesApiKeyStore.getService(serviceID));

    useEffect(() => {
        if (data && qboServiceID) {
            handleQuickBookService(qboServiceID);
            form.setValue('qboServiceID', qboServiceID ?? 'N/A');
        }
        if (data && reinspectionQBOServiceID) {
            handleReinspectionService(reinspectionQBOServiceID);
            form.setValue('reinspectionQboServiceID', reinspectionQBOServiceID ?? 'N/A');
        }
    }, [data, qboServiceID, reinspectionQBOServiceID])

    useEffect(() => {
        if (data) {
            const formattedData = data?.data?.map(item => ({
                value: item.qboServiceID,
                label: item.qboServiceName,
                type: item.qboAccountName,
                isDefault: false
            }));
            const finalData = [
                { value: 'N/A', label: 'N/A', type: 'N/A', isDefault: false },
                ...formattedData
            ];
            setQuickbookOption(finalData)
        }
    }, [data])


    const handleSubmit = async (value: any) => {

        const payload = {
            serviceID: serviceID,
            qboServiceID: quickbookServiceData?.qboServiceID ?? null,
            qboAccountID: quickbookServiceData?.qboAccountID ?? null,
            qboClassID: quickbookServiceData?.qboClassID ?? null,
            reinspectionQBOServiceID: reinspectionServiceData?.qboServiceID ?? null,
            reinspectionQBOAccountID: reinspectionServiceData?.qboAccountID ?? null,
            reinspectionQBOClassID: reinspectionServiceData?.qboClassID ?? null,
        }
        await mutateAsync({ data: payload });
        onClosed();
    }

    const handleQuickBookService = (value: any) => {
        if (value != 'N/A') {
            const result = data?.data?.find(item => item.qboServiceID === value);
            setQuickBookServiceData(result);
        }
        else setQuickBookServiceData(null);
    }

    const handleReinspectionService = (value: any) => {
        if (value != 'N/A') {
            const result = data?.data?.find(item => item.qboServiceID === value);
            setReinspectionServiceData(result);
        }
        else setReinspectionServiceData(null);
    }


    const handleError = (error: any) => {
        const messages = parseErrorMessageRecursive(error);

        let requiredMessages: any[] = [];
        let otherMessages: any[] = [];

        // Classify messages
        Array.isArray(messages) &&
            messages?.forEach((message) => {
                if (message.endsWith('is required')) {
                    requiredMessages.push(message?.split(' is required')[0]?.trim());
                } else {
                    otherMessages.push(message.trim());
                }
            });

        // Remove duplicates
        requiredMessages = Array.from(new Set(requiredMessages));
        otherMessages = Array.from(new Set(otherMessages));

        // Construct the final message
        const finalRequiredMessage = requiredMessages.length > 0 ? `${requiredMessages?.join(', ')} is required` : '';
        const finalOtherMessage = otherMessages?.join(', ');

        // Combine messages
        const finalMessage = [finalRequiredMessage, finalOtherMessage]?.filter(Boolean)?.join(', ');

        // Show the final formatted message
        showSnackbar(`Validation Error: ${finalMessage}`, 'error');
    };

    const handleFormSubmit = form.handleSubmit(handleSubmit as any, handleError);

    const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);
    const isSetQuickBooksServiceMappingPermission = !useAuthorize([UserPermissionName?.SET_QB_SERVICE_MAPPING]);

    return (
        <Dialog
            open={openDialog}
            fullWidth={true}
            maxWidth={'sm'}
            // onClose={handleClose}
            aria-labelledby="dialog-title"
            aria-describedby="dialog-description"
        >
            <DialogTitle id="dialog-title" style={styling.dialogTitleStyle}>
                QuickBooks Service Mapping
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={onClosed}
                sx={{
                    position: 'absolute',
                    right: 10,
                    top: 9,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <Tooltip title={<Typography variant="body1">Close</Typography>}>
                    <CloseIcon />
                </Tooltip>
            </IconButton>
            <DialogContent dividers style={styling.contentPadding} className="popupDilog">
                <ReactHookForm formName="QUICKBOOK_SERVICE_FORM" onSubmit={() => { }} formContextProps={form}>
                    <Grid container spacing={2}>
                        <Grid item lg={12} sm={12} xl={12} xs={12}>
                            <LoadingIndicator isLoading={isLoading} />
                            <FormSelect
                                label="QuickBooks Service"
                                name="qboServiceID"
                                onChange={handleQuickBookService}
                                options={quickbookOption} />
                        </Grid>
                        <Grid item lg={12} sm={12} xl={12} xs={12}>
                            <LoadingIndicator isLoading={isLoading} />
                            <FormSelect
                                label="Reinspection QuickBooks Service"
                                name="reinspectionQboServiceID"
                                onChange={handleReinspectionService}
                                options={quickbookOption} />
                        </Grid>
                        <Grid item lg={12} sm={12} xl={12} xs={12} className="spaceBetween">
                            <Button variant="outlined" style={{ width: '130px' }} onClick={onClosed}>
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                variant="contained"
                                style={{ width: '130px' }}
                                onClick={handleFormSubmit}
                                disabled={
                                    isSetQuickBooksServiceMappingPermission ||
                                    isQBOMappingLoading ||
                                    isReadOnlyPermission}
                            >
                                Save
                            </Button>
                        </Grid>
                    </Grid>
                </ReactHookForm>
            </DialogContent>
        </Dialog >
    );
}